/**
 * "Kutsu sõpru" - self-service for the referral program: invite link + ready-made
 * share text (includes the required disclosure sentence), invite pipeline,
 * account credit balance + history, and the program rules from the server
 * (`GET /api/account/referral`). The tab renders ONLY when the program is on
 * (AccountLayout hides the whole tab on a 404).
 */
import { useEffect, useState } from "react";
import {
  Gift, Copy, Check, Users, Hourglass, CheckCircle2, XCircle, Wallet, ExternalLink,
} from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, type BadgeProps, LoadingState } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtDate, fmtMoney } from "../../lib/format";
import { referral, type ReferralCreditStatus, type ReferralSummary } from "../../lib/referral";

/** Public terms page per language (the public husly.app page, not the account app). */
const TERMS_URL: Record<"et" | "en", string> = {
  // With the locale prefix (husly.app uses the alwaysPrefix strategy, default locale en) -
  // a URL without the prefix would 308 to the default-locale prefix and lose the ET context.
  et: "https://husly.app/et/soovitusprogrammi-tingimused",
  en: "https://husly.app/en/referral-terms",
};

/** Credit status tone (Badge variant). */
function creditStatusVariant(status: ReferralCreditStatus): NonNullable<BadgeProps["variant"]> {
  switch (status) {
    case "vested":
      return "success";
    case "pushed":
      return "info";
    case "voided":
    case "expired":
      return "muted";
    default:
      return "warning"; // pending
  }
}

export function ReferralSection() {
  const t = useT();
  const { locale } = useI18n();
  const [data, setData] = useState<ReferralSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    referral.summary()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">{t("referral.error")}</CardContent>
      </Card>
    );
  }

  // Ready-made share text - includes the required disclosure sentence (receiving a reward).
  const shareText = t("referral.shareText", { link: data.link });
  const termsUrl = TERMS_URL[locale === "et" ? "et" : "en"];
  const money = (cents: number) => fmtMoney(cents, "EUR", locale);
  const cycleLabel = (c: string) => (c === "monthly" || c === "yearly" ? t(`referral.cycle.${c}`) : c);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Gift className="size-5 text-primary" /> {t("referral.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("referral.subtitle")}</p>
      </div>

      {/* ── Invite link + ready-made share text ──────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("referral.linkTitle")}</CardTitle>
          <p className="mt-0.5 text-sm text-muted-foreground">{t("referral.linkHint")}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
              {data.link}
            </code>
            <CopyButton value={data.link} label={t("referral.copyLink")} copiedLabel={t("referral.copied")} />
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("referral.shareTextTitle")}
            </p>
            <p className="mt-1.5 break-words text-sm text-muted-foreground">{shareText}</p>
            <div className="mt-2">
              <CopyButton value={shareText} label={t("referral.copyShareText")} copiedLabel={t("referral.copied")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Invite pipeline ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Users} label={t("referral.stats.invited")} value={data.stats.invited} />
        <StatCard icon={Hourglass} label={t("referral.stats.pending")} value={data.stats.pending} />
        <StatCard icon={CheckCircle2} label={t("referral.stats.credited")} value={data.stats.credited} />
        <StatCard icon={XCircle} label={t("referral.stats.declined")} value={data.stats.declined} />
      </div>

      {/* ── Account credit: balance + history ────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Wallet className="size-4 text-muted-foreground" />
            <CardTitle className="text-base">{t("referral.credits.title")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <BalanceTile label={t("credit.available")} value={money(data.credits.vestedCents)} />
            <BalanceTile label={t("referral.credits.pending")} value={money(data.credits.pendingCents)} />
            <BalanceTile label={t("credit.spent")} value={money(data.credits.spentCents + data.credits.pushedCents)} />
          </div>
          {/* Credit no longer flows onto the invoice automatically - say where it can be spent. */}
          <p className="text-sm text-muted-foreground">{t("credit.spendHint")}</p>
          {data.credits.nextExpiryAt && (
            <p className="text-xs text-muted-foreground">
              {t("credit.expiresAt", { date: fmtDate(data.credits.nextExpiryAt, locale) })}
            </p>
          )}
          {data.credits.rows.length === 0 ? (
            <p className="py-2 text-center text-sm text-muted-foreground">{t("referral.credits.empty")}</p>
          ) : (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                {t("referral.credits.historyTitle")}
              </p>
              <ul className="divide-y divide-border">
                {data.credits.rows.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{t(`referral.kind.${row.kind}`)}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(row.createdAt, locale)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="font-medium tabular-nums">{money(row.amountCents)}</span>
                      <Badge variant={creditStatusVariant(row.status)}>{t(`referral.creditStatus.${row.status}`)}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Rules (from the server, not hardcoded) + terms link ──── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("referral.rules.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>{t("referral.rules.reward", { amount: money(data.rules.rewardCents) })}</li>
            {data.rules.friendRewardCents > 0 && (
              <li>{t("referral.rules.friendReward", { amount: money(data.rules.friendRewardCents) })}</li>
            )}
            <li>{t("referral.rules.minPlan", { plan: data.rules.minPlan })}</li>
            <li>{t("referral.rules.cycles", { cycles: data.rules.cycles.map(cycleLabel).join(", ") })}</li>
            <li>{t("referral.rules.holdDays", { days: data.rules.holdDays })}</li>
            <li>{t("referral.rules.windowDays", { days: data.rules.windowDays })}</li>
          </ul>
          <a
            href={termsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            {t("referral.rules.terms")} <ExternalLink className="size-3.5" />
          </a>
        </CardContent>
      </Card>
    </div>
  );
}

/** Copy button (link/share text) - same pattern as in ApiKeysSection. */
function CopyButton({ value, label, copiedLabel }: { value: string; label: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />} {copied ? copiedLabel : label}
    </Button>
  );
}

/** Pipeline indicator (invited/pending/credited/rejected). */
function StatCard({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card/70 p-4 shadow-soft backdrop-blur">
      <Icon className="size-5 text-muted-foreground" />
      <div className="mt-2 text-2xl font-bold tabular-nums">{value.toLocaleString()}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

/** Credit balance block (pending/confirmed/applied). */
function BalanceTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-bold tabular-nums">{value}</p>
    </div>
  );
}
